import { useCallback, useRef, useState } from 'react'

type SvgUploadResult = {
  svg: string
  colors: string[]
}

export function useSvgUpload(onUpload?: (result: SvgUploadResult) => void) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [colorOptions, setColorOptions] = useState<string[]>([])

  const processSvg = useCallback((svgContent: string): SvgUploadResult => {
    const parser = new DOMParser()
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml')
    const svgElement = svgDoc.documentElement

    // Remove width and height attributes
    svgElement.removeAttribute('width')
    svgElement.removeAttribute('height')

    // Add preserveAspectRatio attribute
    svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet')

    // Set viewBox if it doesn't exist
    if (!svgElement.getAttribute('viewBox')) {
      const width = svgElement.getAttribute('width') || '100'
      const height = svgElement.getAttribute('height') || '100'
      svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`)
    }

    const updatedSvgContent = new XMLSerializer().serializeToString(svgElement)

    // Extract colors from SVG
    const matches = updatedSvgContent
      .match(/#[a-f0-9]{3,6}/gi)
      ?.map(color => color.toUpperCase())
      ?.filter(color => color !== '#000000' && color !== '#FFFFFF' && color !== '#FFF' && color !== '#000')
      .filter((color, index, self) => self.indexOf(color) === index)

    return {
      svg: updatedSvgContent,
      colors: matches || [],
    }
  }, [])

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file?.type !== 'image/svg+xml') {
        console.log('unexpected file type:', file?.type)
        return
      }

      const reader = new FileReader()
      reader.onload = e => {
        if (!e.target?.result) return

        const svgContent = e.target.result as string
        const result = processSvg(svgContent)

        setColorOptions(result.colors)
        onUpload?.(result)
      }
      reader.readAsText(file)
    },
    [processSvg, onUpload]
  )

  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  return {
    fileInputRef,
    colorOptions,
    handleFileChange,
    triggerFileInput,
    processSvg,
  }
}
